import moment from "moment";
import {
  SubscriptionPlanDisplay,
  SubscriptionPlanType,
} from "@shared/types/settings.types";
import { Platform, RequestSource, UtmParams } from "@shared/types/common";
import { ApiResponse } from "@shared/types/requests.types";

export interface UserProperties {
  id: number;
  username: string;
  email: string;
  feature_flags?: string[];
  expiration_timestamp?: number | null;
  original_expiration_timestamp?: number | null;
  full_name: string;
  image: string | null;
  package: SubscriptionPlanDisplay;
  subscription_plan_type: SubscriptionPlanType;
  last_payment_subscription_type: SubscriptionPlanType;
  has_two_factor_auth: boolean;
  has_auto_renewal: boolean;
  is_trial: boolean;
  is_admin: boolean;
  is_telegram_connected: boolean;
  is_discord_connected: boolean;
  telegram_id: string;
  discord_id: string;
  discord_username: string;
  discord_bot_url: string;
  user_uuid: string;
  has_clients_connected: boolean;
  is_email_verified: boolean;
  bot_url: string;
  first_name: string;
  last_name: string;
  registered_to_app: boolean;
  should_track: boolean;
  user_hash: string;
  uuid_user_hash: string;
  is_onboarding: boolean;
  is_mobile_app_user: boolean;
  registration_timestamp: number;
  user_auto_trading_enabled: boolean;
  package_duration: number | null;
  labels: number[];
  has_groups: boolean | null;
  has_active_dca_bots: boolean;
  accepted_terms: boolean;
  accepted_affiliation_terms: boolean;
  has_valid_clients_connected: boolean;
  should_display_nps: boolean;
  should_display_web_nps: boolean;
  in_view_only_grace_period: boolean;
  user_legacy_auto_trading_enabled?: boolean;
  permission_level?: number;
}

export class AuthUser implements UserProperties {
  id: number;
  username: string;
  email: string;
  feature_flags: string[];
  expiration_timestamp?: number | null;
  original_expiration_timestamp?: number | null;
  full_name: string;
  image: string | null;
  package: SubscriptionPlanDisplay;
  has_two_factor_auth: boolean;
  has_auto_renewal: boolean;
  is_trial: boolean;
  is_admin: boolean;
  is_telegram_connected: boolean;
  has_clients_connected: boolean;
  is_email_verified: boolean;
  bot_url: string;
  first_name: string;
  last_name: string;
  registered_to_app: boolean;
  should_track: boolean;
  user_hash: string;
  telegram_id: string;
  discord_id: string;
  discord_bot_url: string;
  is_discord_connected: boolean;
  user_uuid: string;
  uuid_user_hash: string;
  discord_username: string;
  is_onboarding: boolean;
  is_mobile_app_user: boolean;
  registration_timestamp: number;
  user_auto_trading_enabled: boolean;
  package_duration: number | null;
  labels: number[];
  has_groups: boolean | null;
  has_active_dca_bots: boolean;
  accepted_terms: boolean;
  accepted_affiliation_terms: boolean;
  has_valid_clients_connected: boolean;
  should_display_nps: boolean;
  should_display_web_nps: boolean;
  subscription_plan_type: SubscriptionPlanType;
  last_payment_subscription_type: SubscriptionPlanType;
  in_view_only_grace_period: boolean;
  user_legacy_auto_trading_enabled?: boolean;
  permission_level?: number;

  constructor(init: UserProperties) {
    this.id = init.id;
    this.username = init.username;
    this.email = init.email;
    this.feature_flags = init.feature_flags || [];
    this.expiration_timestamp = init.expiration_timestamp;
    this.original_expiration_timestamp = init.original_expiration_timestamp;
    this.full_name = init.full_name;
    this.image = init.image;
    this.package = init.package;
    this.has_two_factor_auth = init.has_two_factor_auth;
    this.has_auto_renewal = init.has_auto_renewal;
    this.is_trial = init.is_trial;
    this.is_admin = init.is_admin;
    this.is_telegram_connected = init.is_telegram_connected;
    this.has_clients_connected = init.has_clients_connected;
    this.is_email_verified = init.is_email_verified;
    this.bot_url = init.bot_url;
    this.first_name = init.first_name;
    this.last_name = init.last_name;
    this.registered_to_app = init.registered_to_app;
    this.should_track = init.should_track;
    this.user_hash = init.user_hash;
    this.telegram_id = init.telegram_id;
    this.discord_id = init.discord_id;
    this.is_discord_connected = init.is_discord_connected;
    this.discord_bot_url = init.discord_bot_url;
    this.user_uuid = init.user_uuid;
    this.uuid_user_hash = init.uuid_user_hash;
    this.discord_username = init.discord_username;
    this.is_onboarding = init.is_onboarding;
    this.is_mobile_app_user = init.is_mobile_app_user;
    this.registration_timestamp = init.registration_timestamp;
    this.user_auto_trading_enabled = init.user_auto_trading_enabled;
    this.package_duration = init.package_duration;
    this.labels = init.labels;
    this.has_groups = init.has_groups;
    this.has_active_dca_bots = init.has_active_dca_bots;
    this.accepted_terms = init.accepted_terms;
    this.accepted_affiliation_terms = init.accepted_affiliation_terms;
    this.has_valid_clients_connected = init.has_valid_clients_connected;
    this.should_display_nps = init.should_display_nps;
    this.should_display_web_nps = init.should_display_web_nps;
    this.subscription_plan_type = init.subscription_plan_type;
    this.last_payment_subscription_type = init.last_payment_subscription_type;
    this.in_view_only_grace_period = init.in_view_only_grace_period;
    this.user_legacy_auto_trading_enabled =
      init.user_legacy_auto_trading_enabled;
    this.permission_level = init.permission_level;
  }

  hasFeatureFlag(featureFlag: string) {
    return this.feature_flags.includes(featureFlag);
  }
  isExpired() {
    if (!this.expiration_timestamp) return false;
    let compareTo = moment().unix();
    if (this.has_auto_renewal) {
      compareTo = moment().subtract(1, "day").unix();
    }
    return this.expiration_timestamp < compareTo;
  }
  isPro() {
    return [SubscriptionPlanType.PRO, SubscriptionPlanType.PREMIUM].includes(
      this.subscription_plan_type
    );
  }
  isLimitedAccess() {
    return this.isExpired() && !this.in_view_only_grace_period;
  }
  hasExternalAccount() {
    return this.is_telegram_connected || this.is_discord_connected;
  }
  isFree() {
    return this.subscription_plan_type === SubscriptionPlanType.FREE;
  }
  getPackageDisplay() {
    return `${this.is_trial ? "Trial - " : ""}${this.package}`;
  }
  isPurchaseSubscription() {
    return this.isFree() || this.isExpired() || this.is_trial;
  }
  isExtendSubscription() {
    return !this?.has_auto_renewal && !this.isPurchaseSubscription();
  }
  isQuotaPlan(plan?: SubscriptionPlanType) {
    return [
      SubscriptionPlanType.FREE,
      SubscriptionPlanType.STARTER,
      SubscriptionPlanType.ADVANCED,
      SubscriptionPlanType.PREMIUM,
      SubscriptionPlanType.PRO_PLUS_NEW,
    ].includes(plan ?? this.subscription_plan_type);
  }
}

export interface LoginRequest {
  email: string;
  password: string;
  two_factor_token?: string;
  captcha_token: string;
  remember_me?: boolean;
  mobile_identifier?: string;
  request_source: RequestSource;
  new_ip_token?: string;
  unique_identifier?: string;
  utm_user_uuid?: string;
}

export interface OAuthUserRequest {
  code: string;
  oauth_platform: OAuthPlatformType;
  captcha_token: string;
  two_factor_token?: string;
  new_ip_token?: string;
  unique_identifier?: string;
  mobile_identifier?: string;
  utm_user_uuid?: string;
}

export interface LoginResponse {
  token: string;
  refresh_token: string;
  challenge?: LoginChallenge;
}

export interface LoginResponseWithUserId extends LoginResponse {
  user_id: number;
}

export interface RefreshTokenRequest {
  refresh_token: string;
}

export interface RefreshTokenResponse {
  token: string;
}

export interface RegisterRequest {
  first_name: string;
  last_name: string;
  email: string;
  password: string;
  campaign_id?: string;
  referral_code?: string;
  request_source: RequestSource;
  captcha_token?: string;
  use_captcha?: boolean;
  utm_user_uuid?: string;
}

export interface ResetPasswordRequest {
  token: string;
  password: string;
}

export interface ChangePasswordRequest {
  old_password: string;
  new_password: string;
}

export interface ChangeUserFullNameRequest {
  first_name: string;
  last_name: string;
}

export interface ForgotPasswordRequest {
  email: string;
  request_source: RequestSource;
}

export interface GenerateQRCodeRequest {
  refresh_token?: string;
}

export interface GenerateQRCodeResponse {
  qrcode: string;
  setup_key: string;
}

export interface DisableTwoFARequest {
  two_factor_token: string;
}

export type StoreTwoFARequest = Pick<GenerateQRCodeRequest, "refresh_token"> &
  Pick<DisableTwoFARequest, "two_factor_token">;

export interface GetCaptchaResponse {
  challenge: string;
  gt: string;
  success: number;
}

export interface ValidateCaptchaRequest {
  challenge: string;
  validate: string;
  seccode: string;
}

export interface ReferralRequest {
  referral_code: string;
}

export interface DeleteCornixAccountConfirmationEmailRequest {
  email: string;
  request_source: RequestSource;
}

export interface ValidateCaptchaResponse {
  success: number;
  captcha_token?: string;
}

export type UserBasicDetails = Pick<
  UserProperties,
  "first_name" | "last_name" | "username" | "image"
>;

export type StorageOauthUserData = UserBasicDetails & { uuid: string };
export type StorageOAuthData = { [userId in number]: StorageOauthUserData } & {
  [userEmail in string]: StorageOauthUserData;
};

export interface VerifyConfirmEmailCodeRequest {
  auth_code: number;
  email_validator_uuid?: string;
  request_source?: RequestSource;
  mobile_identifier?: string;
  unique_identifier?: string;
}

export interface ResendRegistrationEmailRequest {
  email?: string;
  email_validator_uuid?: string;
  request_source: RequestSource;
}

export type ConnectToTelegramRequest = { auth_code: string } & (
  | { user_short_uuid: string }
  | { uuid: string }
);

export enum UserSource {
  TELEGRAM = "telegram",
  DISCORD = "discord",
  DIRECT = "direct",
}

export enum ExternalAccountType {
  TELEGRAM = "Telegram",
  DISCORD = "Discord",
}

export enum OAuthPlatformType {
  GOOGLE = 1,
  APPLE = 2,
  FACEBOOK = 3,
  TELEGRAM = 4,
}

export interface VerifyPersonalDetailsRequest {
  uuid: string;
  first_name: string;
  last_name: string;
  username: string;
  image: string;
}
export type SendConnectToTelegramAuthCodeRequest = {
  platform: Platform;
} & ({ user_short_uuid: string } | { uuid: string });

export enum LoginChallenge {
  TWO_FA = 1,
  NEW_IP = 2,
}

export type UtmData = { utm_user_uuid: string } & {
  [key in UtmParams]?: string;
};

export type MarkVisitRequest = Omit<UtmData, "gclid"> & {
  click_id?: string;
};

export interface TelegramWebappQueryData {
  path?: string;
  parameters?: Record<string, string>;
}

export interface WebAppUser {
  first_name: string;
  last_name?: string;
  user?: string;
  is_premium?: boolean;
  added_to_attachment_menu?: boolean;
  allows_write_to_pm?: boolean;
}

export interface ValidateWebAppInitDataRequest {
  init_data: string;
  source?: string;
  source_button?: string;
}

export type ValidateWebAppInitDataResponse = ApiResponse<{
  is_new_user: boolean;
}> &
  LoginResponse;

export interface ForgotPasswordRequest {
  email: string;
  request_source: RequestSource;
}
